import { Box, Stack } from '@mui/material';
import React, { useContext } from 'react';
import { spaces } from '../../../assets/styles/themes/tokens';
import SearchFilterButton from './SearchFilterButton';
import { useTranslation } from 'react-i18next';
import useQueryModification from '../../../routes/SearchPage/useQueryModification';
import { SearchFilters } from '../../../types/search';
import useRouter from '../../../utils/useRouter';
import SortByFilterNewUI from '../../SearchFilters/SortByFilterNewUI';
import { searchSettings } from '../../../constants';
import MoreFiltersButton from './MoreFiltersButton';
import { SearchFiltersDialogContext } from '../../../state/SearchFiltersDialogProvider';
import { AccordionType } from '../../modals/SearchFiltersDialog/SearchFiltersDialog';
import useScreenSize from '../../../utils/useScreenSize';

type SearchFiltersKeys = keyof Pick<
  SearchFilters,
  | 'classYear'
  | 'subject'
  | 'publisher'
  | 'schoolType'
  | 'workload'
  | 'teachingFormat'
  | 'learningFormat'
  | 'contentType'
  | 'subTopic'
  | 'courseTheme'
  | 'courseType'
  | 'courseDuration'
>;

interface SearchFiltersContainerProps {
  requestFilters: SearchFilters;
  visibleKeys: Array<SearchFiltersKeys>;
  invisibleKeys: Array<SearchFiltersKeys>,
  dialogFiltersOrder: Array<AccordionType>
  dialogFiltersType: 'core' | 'course'
}

const filterLabels: Record<SearchFiltersKeys, string> = {
  classYear: 'Class years',
  subject: 'Subject',
  publisher: 'Publisher',
  schoolType: 'SchoolType',
  workload: 'Workload',
  teachingFormat: 'Teaching format',
  learningFormat: 'Learning format',
  contentType: 'Content type',
  subTopic: 'Subtopic',
  courseTheme: 'Course theme',
  courseType: 'Course type',
  courseDuration: 'Course duration'
};

const SearchFiltersContainer: React.FC<SearchFiltersContainerProps> = (
  props
) => {
  const { requestFilters, visibleKeys, invisibleKeys, dialogFiltersOrder, dialogFiltersType } = props;

  const { t } = useTranslation();
  const { router, match } = useRouter();
  const { isSmallScreen } = useScreenSize();
  const { updateQueryParams } = useQueryModification<SearchFilters>(router, match);
  const { openSearchFiltersDialog } = useContext(SearchFiltersDialogContext);

  if (isSmallScreen) invisibleKeys.push(...visibleKeys);

  const numberOfInvisibleFiltersSelected = invisibleKeys.reduce(
    (acc, key: keyof SearchFilters) => {
      const value = requestFilters[key];
      if (Array.isArray(value)) {
        return acc + value.length;
      } else if (typeof value === 'string') {
        return acc + 1;
      } else {
        return acc;
      }
    },
    0
  );

  return (
    <Stack
      direction='row'
      data-testid='search-filters'
      sx={{
        display: 'flex',
        alignItems: 'center',
        alignContent: 'flex-start',
        gap: spaces.xs,
        alignSelf: 'stretch',
      }}
    >
      {!isSmallScreen && (
        <>
          {visibleKeys.map((key) => (
            <SearchFilterButton
              key={key}
              text={t(filterLabels[key])}
              numberOfSelections={requestFilters[key]?.length}
              onClick={() => openSearchFiltersDialog(key as AccordionType, dialogFiltersOrder, dialogFiltersType)}
            />
          ))}
        </>
      )}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <MoreFiltersButton
          numberOfSelections={numberOfInvisibleFiltersSelected}
          onClick={() => openSearchFiltersDialog(undefined, dialogFiltersOrder, dialogFiltersType)}
        />
      </Box>
      {isSmallScreen && dialogFiltersType === 'core' && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignSelf: 'stretch',
            minWidth: 0,
            '& .MuiInputBase-root': {
              height: '100%',
              width: 'unset !important',
            },
            '& .MuiFormControl-root': {
              height: '100%',
              width: '100%',
            },
            '& .MuiListItemText-primary': {
              maxWidth: 'unset !important',
            },
          }}
        >
          <SortByFilterNewUI
            currentValue={requestFilters.sortBy}
            updateFilter={(sortBy) => updateQueryParams({ sortBy })}
            clearFilter={() => updateQueryParams({ sortBy: searchSettings.defaultSortBy })}
          />
        </Box>
      )}
    </Stack>
  );
};

export default SearchFiltersContainer;
